import {
  AlertCircle,
  AlertTriangle,
  ArrowUpDown,
  CheckCircle2,
  ChevronRight,
  FileJson2,
  FileSpreadsheet,
  Info,
  RefreshCw,
  RotateCcw,
  Search,
  Settings2,
  SlidersHorizontal,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Button } from '../../components/Button';
import {
  DEFAULT_INSPECTION_SETTINGS,
  FIELD_DEFINITIONS,
  getCellText,
  validateInspectionSettings,
  type InspectionSettings,
  type Issue,
  type Severity,
  type WorkbookData,
} from '../../domain';
import { RULES } from '../../rules';
import type { InspectionPayload } from '../../workers/protocol';

type SeverityFilter = 'all' | Severity;
const ISSUE_PAGE_SIZE = 100;

const SEVERITY_LABEL: Readonly<Record<Severity, string>> = {
  error: '错误',
  warning: '警告',
  info: '提示',
};

const SEVERITY_CLASSES: Readonly<Record<Severity, string>> = {
  error: 'border-red-200 bg-red-50 text-red-800',
  warning: 'border-amber-200 bg-amber-50 text-amber-900',
  info: 'border-sky-200 bg-sky-50 text-sky-900',
};

export interface ResultsScreenProps {
  readonly fileName: string;
  readonly payload: InspectionPayload;
  readonly settings: InspectionSettings;
  readonly error: string | null;
  readonly onReinspect: (settings: InspectionSettings) => void;
  readonly onChangeFile: () => void;
  readonly onExportXlsx: () => Promise<void>;
  readonly onExportJson: () => Promise<void>;
}

export function ResultsScreen({
  fileName,
  payload,
  settings,
  error,
  onReinspect,
  onChangeFile,
  onExportXlsx,
  onExportJson,
}: ResultsScreenProps) {
  const [severity, setSeverity] = useState<SeverityFilter>('all');
  const [ruleId, setRuleId] = useState('all');
  const [sheetName, setSheetName] = useState('all');
  const [query, setQuery] = useState('');
  const [ascending, setAscending] = useState(true);
  const [selectedIssue, setSelectedIssue] = useState<Issue | null>(null);
  const [exporting, setExporting] = useState<'xlsx' | 'json' | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const [draftSettings, setDraftSettings] = useState(settings);
  const [page, setPage] = useState(1);
  const closeIssue = useCallback(() => setSelectedIssue(null), []);

  const filteredIssues = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase('zh-CN');
    return [...payload.result.issues]
      .filter((issue) => severity === 'all' || issue.severity === severity)
      .filter((issue) => ruleId === 'all' || issue.ruleId === ruleId)
      .filter((issue) => sheetName === 'all' || issue.sheetName === sheetName)
      .filter((issue) => {
        if (normalizedQuery.length === 0) return true;
        return [issue.code, issue.name, issue.message, issue.suggestion]
          .join(' ')
          .toLocaleLowerCase('zh-CN')
          .includes(normalizedQuery);
      })
      .sort((left, right) => {
        const rowDifference = left.excelRow - right.excelRow;
        if (rowDifference !== 0) return ascending ? rowDifference : -rowDifference;
        return left.ruleId.localeCompare(right.ruleId);
      });
  }, [ascending, payload.result.issues, query, ruleId, severity, sheetName]);

  const clearFilters = () => {
    setSeverity('all');
    setRuleId('all');
    setSheetName('all');
    setQuery('');
    setPage(1);
  };

  const hasFilters = severity !== 'all' || ruleId !== 'all' || sheetName !== 'all' || query !== '';
  const summary = payload.result.summary;
  const pageCount = Math.max(1, Math.ceil(filteredIssues.length / ISSUE_PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const pageStart = (currentPage - 1) * ISSUE_PAGE_SIZE;
  const visibleIssues = filteredIssues.slice(pageStart, pageStart + ISSUE_PAGE_SIZE);

  return (
    <main className="min-h-[calc(100dvh-7rem)] bg-slate-100">
      <div className="mx-auto max-w-[1440px] px-4 py-6 sm:px-6 lg:px-8">
        <section className="rounded-xl border border-slate-300 bg-slate-800 p-5 text-white shadow-[0_18px_50px_rgba(31,52,72,0.14)] sm:p-6">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-sm font-semibold text-emerald-300">
                <CheckCircle2 aria-hidden="true" className="size-5" />
                检查完成
              </div>
              <h1
                className="mt-3 truncate text-2xl font-black tracking-tight sm:text-3xl"
                title={fileName}
              >
                {fileName}
              </h1>
              <p className="mt-2 text-sm text-slate-300">
                {summary.sheetCount} 个工作表，识别 {summary.detailRowCount} 条明细，耗时{' '}
                {payload.durationMs} ms
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="secondary"
                icon={<RefreshCw aria-hidden="true" className="size-4" />}
                onClick={() => onReinspect(draftSettings)}
              >
                重新检查
              </Button>
              <Button
                variant="ghost"
                className="border-slate-500 text-white hover:bg-slate-700"
                icon={<RotateCcw aria-hidden="true" className="size-4" />}
                onClick={onChangeFile}
              >
                更换文件
              </Button>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-slate-600 bg-slate-600 sm:grid-cols-4">
            <Metric label="明细行" value={summary.detailRowCount} className="bg-slate-700" />
            <Metric
              label="错误"
              value={summary.errorCount}
              className="bg-red-950/70 text-red-100"
              onClick={() => setSeverity('error')}
            />
            <Metric
              label="警告"
              value={summary.warningCount}
              className="bg-amber-950/60 text-amber-100"
              onClick={() => setSeverity('warning')}
            />
            <Metric
              label="提示"
              value={summary.infoCount}
              className="bg-sky-950/60 text-sky-100"
              onClick={() => setSeverity('info')}
            />
          </div>
        </section>

        {error !== null && (
          <div
            role="alert"
            className="mt-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm leading-6 text-red-800"
          >
            {error}
          </div>
        )}

        <section className="mt-5 rounded-xl border border-slate-300 bg-white shadow-sm">
          <div className="border-b border-slate-200 p-4 sm:p-5">
            <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
              <div>
                <h2 className="text-xl font-black text-slate-900">问题台账</h2>
                <p className="mt-1 text-sm text-slate-500">
                  {filteredIssues.length <= ISSUE_PAGE_SIZE
                    ? `当前显示 ${String(filteredIssues.length)} 条，共检出 ${String(payload.result.issues.length)} 条。`
                    : `筛选后 ${String(filteredIssues.length)} 条，本页显示 ${String(visibleIssues.length)} 条，共检出 ${String(payload.result.issues.length)} 条。`}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  loading={exporting === 'xlsx'}
                  disabled={exporting !== null}
                  icon={<FileSpreadsheet aria-hidden="true" className="size-4" />}
                  onClick={() => void runExport('xlsx', onExportXlsx, setExporting, setExportError)}
                >
                  导出 XLSX
                </Button>
                <Button
                  loading={exporting === 'json'}
                  disabled={exporting !== null}
                  icon={<FileJson2 aria-hidden="true" className="size-4" />}
                  onClick={() => void runExport('json', onExportJson, setExporting, setExportError)}
                >
                  导出 JSON
                </Button>
              </div>
            </div>

            {exportError !== null && (
              <div
                role="alert"
                className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm leading-6 text-red-800"
              >
                {exportError}
              </div>
            )}

            <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-[1.4fr_0.8fr_0.9fr_0.9fr_auto]">
              <label className="relative block">
                <span className="sr-only">搜索问题</span>
                <Search
                  aria-hidden="true"
                  className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-slate-400"
                />
                <input
                  type="search"
                  value={query}
                  onChange={(event) => {
                    setQuery(event.target.value);
                    setPage(1);
                  }}
                  placeholder="搜索编码、名称或问题内容"
                  className="h-11 w-full rounded-lg border border-slate-300 bg-white pr-3 pl-10 text-sm outline-none placeholder:text-slate-500 focus:border-orange-600 focus:ring-2 focus:ring-orange-100"
                />
              </label>
              <FilterSelect
                label="严重等级"
                value={severity}
                onChange={(value) => {
                  setSeverity(value as SeverityFilter);
                  setPage(1);
                }}
                options={[
                  ['all', '全部等级'],
                  ['error', '错误'],
                  ['warning', '警告'],
                  ['info', '提示'],
                ]}
              />
              <FilterSelect
                label="检查规则"
                value={ruleId}
                onChange={(value) => {
                  setRuleId(value);
                  setPage(1);
                }}
                options={[
                  ['all', '全部规则'],
                  ...RULES.map((rule) => [rule.id, `${rule.id} ${rule.name}`] as const),
                ]}
              />
              <FilterSelect
                label="工作表"
                value={sheetName}
                onChange={(value) => {
                  setSheetName(value);
                  setPage(1);
                }}
                options={[
                  ['all', '全部工作表'],
                  ...payload.workbook.sheets.map((sheet) => [sheet.name, sheet.name] as const),
                ]}
              />
              <Button
                variant="ghost"
                disabled={!hasFilters}
                icon={<X aria-hidden="true" className="size-4" />}
                onClick={clearFilters}
              >
                清除筛选
              </Button>
            </div>
          </div>

          {filteredIssues.length === 0 ? (
            <EmptyResults hasFilters={hasFilters} onClear={clearFilters} />
          ) : (
            <>
              <div className="hidden overflow-x-auto md:block" tabIndex={0}>
                <table className="min-w-[1320px] w-full border-collapse text-left text-xs">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      {['序号', '等级', '规则编号', '工作表'].map((label) => (
                        <th
                          key={label}
                          scope="col"
                          className="border-b border-slate-200 px-3 py-3 font-bold"
                        >
                          {label}
                        </th>
                      ))}
                      <th scope="col" className="border-b border-slate-200 px-3 py-3 font-bold">
                        <button
                          type="button"
                          onClick={() => {
                            setAscending((value) => !value);
                            setPage(1);
                          }}
                          className="inline-flex items-center gap-1 rounded outline-none hover:text-slate-900 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-600"
                        >
                          Excel 行号 <ArrowUpDown aria-hidden="true" className="size-3.5" />
                        </button>
                      </th>
                      {['清单编码', '项目名称', '问题字段', '当前值', '问题说明', '处理建议'].map(
                        (label) => (
                          <th
                            key={label}
                            scope="col"
                            className="border-b border-slate-200 px-3 py-3 font-bold"
                          >
                            {label}
                          </th>
                        ),
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {visibleIssues.map((issue, index) => (
                      <tr key={issue.id} className="group bg-white align-top hover:bg-slate-50">
                        <td className="border-b border-slate-200 px-3 py-3 text-slate-500">
                          {pageStart + index + 1}
                        </td>
                        <td className="border-b border-slate-200 px-3 py-3">
                          <SeverityBadge severity={issue.severity} />
                        </td>
                        <td className="border-b border-slate-200 px-3 py-3 font-mono font-bold text-slate-800">
                          {issue.ruleId}
                        </td>
                        <td className="border-b border-slate-200 px-3 py-3 text-slate-700">
                          {issue.sheetName}
                        </td>
                        <td className="border-b border-slate-200 px-3 py-3 font-mono font-bold text-slate-900">
                          <button
                            type="button"
                            className="rounded underline decoration-slate-300 underline-offset-4 outline-none hover:text-orange-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-600"
                            onClick={() => setSelectedIssue(issue)}
                          >
                            {issue.excelRow}
                          </button>
                        </td>
                        <td className="max-w-40 border-b border-slate-200 px-3 py-3 font-mono break-all text-slate-700">
                          {issue.code || '未提供'}
                        </td>
                        <td className="max-w-52 border-b border-slate-200 px-3 py-3 font-semibold text-slate-800">
                          {issue.name || '未提供'}
                        </td>
                        <td className="border-b border-slate-200 px-3 py-3 text-slate-600">
                          {fieldLabel(issue)}
                        </td>
                        <td className="max-w-48 border-b border-slate-200 px-3 py-3 break-words text-slate-700">
                          {issue.currentValue || '空值'}
                        </td>
                        <td className="max-w-72 border-b border-slate-200 px-3 py-3 leading-5 text-slate-800">
                          {issue.message}
                        </td>
                        <td className="max-w-72 border-b border-slate-200 px-3 py-3 leading-5 text-slate-600">
                          <button
                            type="button"
                            className="group/button flex w-full items-start justify-between gap-2 rounded text-left outline-none hover:text-slate-900 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-600"
                            onClick={() => setSelectedIssue(issue)}
                          >
                            <span>{issue.suggestion}</span>
                            <ChevronRight
                              aria-hidden="true"
                              className="mt-0.5 size-4 shrink-0 transition group-hover/button:translate-x-0.5"
                            />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="divide-y divide-slate-200 md:hidden">
                {visibleIssues.map((issue) => (
                  <button
                    key={issue.id}
                    type="button"
                    onClick={() => setSelectedIssue(issue)}
                    className="block w-full p-4 text-left outline-none hover:bg-slate-50 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-orange-600"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <SeverityBadge severity={issue.severity} />
                      <span className="font-mono text-xs font-bold text-slate-500">
                        {issue.ruleId} · 行 {issue.excelRow}
                      </span>
                    </div>
                    <p className="mt-3 text-sm font-bold text-slate-900">
                      {issue.name || issue.code || '未命名清单项'}
                    </p>
                    <p className="mt-2 text-sm leading-6 text-slate-700">{issue.message}</p>
                  </button>
                ))}
              </div>

              {pageCount > 1 && (
                <nav
                  aria-label="问题分页"
                  className="flex flex-col items-center justify-between gap-3 border-t border-slate-200 px-4 py-4 sm:flex-row"
                >
                  <p className="text-sm text-slate-600">
                    第 {currentPage} / {pageCount} 页，每页最多 {ISSUE_PAGE_SIZE} 条
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      disabled={currentPage === 1}
                      onClick={() => setPage(Math.max(1, currentPage - 1))}
                    >
                      上一页
                    </Button>
                    <Button
                      variant="ghost"
                      disabled={currentPage === pageCount}
                      onClick={() => setPage(Math.min(pageCount, currentPage + 1))}
                    >
                      下一页
                    </Button>
                  </div>
                </nav>
              )}
            </>
          )}
        </section>

        <RuleSettingsPanel
          settings={draftSettings}
          onSettingsChange={setDraftSettings}
          onReinspect={() => onReinspect(draftSettings)}
        />
      </div>

      {selectedIssue !== null && (
        <IssueDrawer issue={selectedIssue} workbook={payload.workbook} onClose={closeIssue} />
      )}
    </main>
  );
}

function Metric({
  label,
  value,
  className,
  onClick,
}: {
  readonly label: string;
  readonly value: number;
  readonly className: string;
  readonly onClick?: () => void;
}) {
  const content = (
    <>
      <span className="text-xs font-semibold opacity-75">{label}</span>
      <strong className="mt-1 block font-mono text-2xl">{value}</strong>
    </>
  );
  return onClick === undefined ? (
    <div className={`p-4 ${className}`}>{content}</div>
  ) : (
    <button
      type="button"
      onClick={onClick}
      className={`p-4 text-left outline-none transition hover:brightness-110 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-orange-400 ${className}`}
    >
      {content}
    </button>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly options: readonly (readonly [string, string])[];
}) {
  return (
    <label>
      <span className="sr-only">{label}</span>
      <select
        aria-label={label}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-11 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-800 outline-none focus:border-orange-600 focus:ring-2 focus:ring-orange-100"
      >
        {options.map(([optionValue, optionLabel]) => (
          <option key={optionValue} value={optionValue}>
            {optionLabel}
          </option>
        ))}
      </select>
    </label>
  );
}

function SeverityBadge({ severity }: { readonly severity: Severity }) {
  const Icon = severity === 'error' ? AlertCircle : severity === 'warning' ? AlertTriangle : Info;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-bold ${SEVERITY_CLASSES[severity]}`}
    >
      <Icon aria-hidden="true" className="size-3.5" />
      {SEVERITY_LABEL[severity]}
    </span>
  );
}

function EmptyResults({
  hasFilters,
  onClear,
}: {
  readonly hasFilters: boolean;
  readonly onClear: () => void;
}) {
  return (
    <div className="px-6 py-16 text-center">
      <CheckCircle2 aria-hidden="true" className="mx-auto size-10 text-emerald-700" />
      <h3 className="mt-4 text-lg font-black text-slate-900">
        {hasFilters ? '没有符合当前筛选的问题' : '未发现已启用规则范围内的问题'}
      </h3>
      <p className="mx-auto mt-2 max-w-lg text-sm leading-6 text-slate-600">
        {hasFilters
          ? '调整筛选条件或清除筛选后查看全部结果。'
          : '这不代表清单完全正确，仍需结合项目资料和专业判断进行人工复核。'}
      </p>
      {hasFilters && (
        <Button
          className="mt-5"
          icon={<X aria-hidden="true" className="size-4" />}
          onClick={onClear}
        >
          清除筛选
        </Button>
      )}
    </div>
  );
}

function RuleSettingsPanel({
  settings,
  onSettingsChange,
  onReinspect,
}: {
  readonly settings: InspectionSettings;
  readonly onSettingsChange: (settings: InspectionSettings) => void;
  readonly onReinspect: () => void;
}) {
  const validationMessages = validateInspectionSettings(settings);
  const updateTolerance = (key: 'absolute' | 'relative', value: string) =>
    onSettingsChange({
      ...settings,
      amountTolerance: { ...settings.amountTolerance, [key]: value },
    });
  return (
    <details className="mt-5 rounded-xl border border-slate-300 bg-white shadow-sm">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-4 rounded-xl px-4 py-4 font-bold text-slate-900 outline-none hover:bg-slate-50 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-orange-600 sm:px-5">
        <span className="inline-flex items-center gap-2">
          <Settings2 aria-hidden="true" className="size-5 text-slate-500" />
          规则与容差设置
        </span>
        <SlidersHorizontal aria-hidden="true" className="size-5 text-slate-400" />
      </summary>
      <div className="border-t border-slate-200 p-4 sm:p-5">
        <div className="grid gap-4 sm:grid-cols-3">
          <NumberSetting
            label="固定金额容差（元）"
            value={settings.amountTolerance.absolute}
            onChange={(value) => updateTolerance('absolute', value)}
            step="0.01"
          />
          <NumberSetting
            label="相对金额容差（%）"
            value={String(Number(settings.amountTolerance.relative) * 100)}
            onChange={(value) => updateTolerance('relative', String((Number(value) || 0) / 100))}
            step="0.01"
          />
          <NumberSetting
            label="价格偏离阈值（%）"
            value={String(Number(settings.priceDeviationThreshold) * 100)}
            onChange={(value) =>
              onSettingsChange({
                ...settings,
                priceDeviationThreshold: String((Number(value) || 0) / 100),
              })
            }
            step="1"
          />
        </div>
        <div className="mt-6 grid gap-2 md:grid-cols-2">
          {RULES.map((rule) => {
            const enabled = settings.enabledRules[rule.id] ?? rule.enabled;
            return (
              <label
                key={rule.id}
                className="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-200 px-3 py-3 hover:bg-slate-50"
              >
                <input
                  type="checkbox"
                  className="mt-0.5 size-4 accent-orange-600"
                  checked={enabled}
                  onChange={(event) =>
                    onSettingsChange({
                      ...settings,
                      enabledRules: { ...settings.enabledRules, [rule.id]: event.target.checked },
                    })
                  }
                />
                <span className="min-w-0">
                  <strong className="block text-sm text-slate-900">
                    {rule.id} {rule.name}
                  </strong>
                  <span className="mt-1 block text-xs leading-5 text-slate-500">
                    {rule.description}
                  </span>
                </span>
              </label>
            );
          })}
        </div>
        {validationMessages.length > 0 && (
          <div
            role="alert"
            className="mt-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm leading-6 text-red-800"
          >
            <ul className="space-y-1">
              {validationMessages.map((message) => (
                <li key={message}>{message}</li>
              ))}
            </ul>
          </div>
        )}
        <div className="mt-5 flex flex-wrap justify-end gap-2">
          <Button variant="ghost" onClick={() => onSettingsChange(DEFAULT_INSPECTION_SETTINGS)}>
            恢复默认
          </Button>
          <Button
            variant="primary"
            disabled={validationMessages.length > 0}
            icon={<RefreshCw aria-hidden="true" className="size-4" />}
            onClick={onReinspect}
          >
            应用并重新检查
          </Button>
        </div>
      </div>
    </details>
  );
}

function NumberSetting({
  label,
  value,
  onChange,
  step,
}: {
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly step: string;
}) {
  return (
    <label className="text-sm font-semibold text-slate-700">
      {label}
      <input
        type="number"
        min="0"
        step={step}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="mt-2 h-11 w-full rounded-lg border border-slate-300 bg-white px-3 font-mono font-normal outline-none focus:border-orange-600 focus:ring-2 focus:ring-orange-100"
      />
    </label>
  );
}

function IssueDrawer({
  issue,
  workbook,
  onClose,
}: {
  readonly issue: Issue;
  readonly workbook: WorkbookData;
  readonly onClose: () => void;
}) {
  const dialogRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const previouslyFocused =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const dialog = dialogRef.current;
    if (dialog === null) return;

    const focusableSelector =
      'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const focusable = () => [...dialog.querySelectorAll<HTMLElement>(focusableSelector)];
    focusable()[0]?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;
      const items = focusable();
      const first = items[0];
      const last = items.at(-1);
      if (first === undefined || last === undefined) {
        event.preventDefault();
        dialog.focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      previouslyFocused?.focus();
    };
  }, [onClose]);

  const row = workbook.sheets
    .find((sheet) => sheet.name === issue.sheetName)
    ?.rows.find((candidate) => candidate.excelRow === issue.excelRow);
  return (
    <div
      className="fixed inset-0 z-40 bg-slate-950/40"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <aside
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="issue-title"
        tabIndex={-1}
        className="ml-auto flex h-full w-full max-w-xl flex-col overflow-hidden bg-white shadow-[-20px_0_70px_rgba(15,23,42,0.22)]"
      >
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-5">
          <div>
            <SeverityBadge severity={issue.severity} />
            <h2 id="issue-title" className="mt-3 text-xl font-black text-slate-900">
              {issue.ruleId} {issue.ruleName}
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              {issue.sheetName}，Excel 第 {issue.excelRow} 行
            </p>
          </div>
          <button
            type="button"
            aria-label="关闭问题详情"
            onClick={onClose}
            className="grid size-10 shrink-0 place-items-center rounded-lg text-slate-500 outline-none hover:bg-slate-100 hover:text-slate-900 focus-visible:outline-2 focus-visible:outline-orange-600"
          >
            <X aria-hidden="true" className="size-5" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-5">
          <section>
            <h3 className="text-sm font-bold text-slate-900">问题说明</h3>
            <p className="mt-2 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm leading-6 text-slate-800">
              {issue.message}
            </p>
          </section>
          <section className="mt-6">
            <h3 className="text-sm font-bold text-slate-900">处理建议</h3>
            <p className="mt-2 text-sm leading-6 text-slate-700">{issue.suggestion}</p>
          </section>
          <section className="mt-6">
            <h3 className="text-sm font-bold text-slate-900">当前行关键字段</h3>
            <dl className="mt-2 overflow-hidden rounded-lg border border-slate-200">
              {FIELD_DEFINITIONS.map((field) => (
                <div
                  key={field.key}
                  className="grid grid-cols-[110px_minmax(0,1fr)] border-b border-slate-200 px-3 py-2 text-sm last:border-b-0"
                >
                  <dt className="font-semibold text-slate-500">{field.label}</dt>
                  <dd className="min-w-0 break-words text-slate-800">
                    {row === undefined ? '未读取' : getCellText(row, field.key) || '空值'}
                  </dd>
                </div>
              ))}
            </dl>
          </section>
          {issue.relatedRows.length > 0 && (
            <section className="mt-6">
              <h3 className="text-sm font-bold text-slate-900">关联行</h3>
              <p className="mt-2 text-sm text-slate-700">
                同一工作表中的 Excel 行号：{issue.relatedRows.join('、')}
              </p>
            </section>
          )}
        </div>
      </aside>
    </div>
  );
}

function fieldLabel(issue: Issue): string {
  return issue.field === null
    ? '多个字段'
    : (FIELD_DEFINITIONS.find((field) => field.key === issue.field)?.label ?? issue.field);
}

async function runExport(
  type: 'xlsx' | 'json',
  action: () => Promise<void>,
  setExporting: (value: 'xlsx' | 'json' | null) => void,
  setError: (value: string | null) => void,
): Promise<void> {
  setError(null);
  setExporting(type);
  try {
    await action();
  } catch (error) {
    setError(error instanceof Error ? error.message : '报告导出失败，请重试。');
  } finally {
    setExporting(null);
  }
}
